import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import { getAllProjects, getTodayDate, formatDate } from '../../../services/timesheetService'
import { getTotalWorkedHours } from '../../../services/attendanceService'
import { getTotalAllocatedHours } from '../../../services/timesheetService'

/**
 * TimesheetEntryModal Component
 * Modal form for creating/editing timesheet entries
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit form callback
 * @param {object} userData - Current user data
 * @param {boolean} isLoading - Whether form is submitting
 * @param {object} editEntry - Entry to edit (null for create mode)
 * @param {string} selectedDate - Default date (YYYY-MM-DD)
 */
const TimesheetEntryModal = ({
  isOpen,
  onClose,
  onSubmit,
  userData,
  isLoading = false,
  editEntry = null,
  selectedDate = null
}) => {
  // Form state
  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState('')
  const [date, setDate] = useState(selectedDate || getTodayDate())
  const [hours, setHours] = useState('')
  const [taskDescription, setTaskDescription] = useState('')
  const [error, setError] = useState('')

  // Attendance data state
  const [workedHours, setWorkedHours] = useState(0)
  const [allocatedHours, setAllocatedHours] = useState(0)
  const [hasCheckOut, setHasCheckOut] = useState(false)
  const [checkingHours, setCheckingHours] = useState(false)

  // Character limit
  const descriptionMaxLength = 500

  // Calculate max date (today)
  const maxDate = getTodayDate()

  // Calculate min date (30 days ago)
  const getMinDate = () => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    return thirtyDaysAgo.toISOString().split('T')[0]
  }

  const minDate = getMinDate()

  // Load projects on mount
  useEffect(() => {
    if (isOpen) {
      loadProjects()
    }
  }, [isOpen])

  // Load attendance hours when date or editEntry changes
  useEffect(() => {
    if (isOpen && date && userData?.employeeId) {
      loadAttendanceHours()
    }
  }, [date, isOpen, userData])

  // Populate form if editing
  useEffect(() => {
    if (isOpen && editEntry) {
      setProjectId(editEntry.projectId)
      setDate(editEntry.date)
      setHours(editEntry.hours.toString())
      setTaskDescription(editEntry.taskDescription)
    } else if (isOpen) {
      // Reset for create mode
      resetForm()
      if (selectedDate) {
        setDate(selectedDate)
      }
    }
  }, [isOpen, editEntry, selectedDate])

  const loadProjects = async () => {
    const result = await getAllProjects()
    if (result.success) {
      setProjects(result.data)
      // Set first project as default if not editing
      if (!editEntry && result.data.length > 0) {
        setProjectId(result.data[0].id)
      }
    } else {
      setError('Failed to load projects')
    }
  }

  const loadAttendanceHours = async () => {
    if (!userData?.employeeId || !date) return

    setCheckingHours(true)

    try {
      // Get worked hours
      const workedResult = await getTotalWorkedHours(userData.employeeId, date)

      if (workedResult.success) {
        setWorkedHours(workedResult.hours || 0)
        setHasCheckOut(workedResult.hasCheckOut)
      }

      // Get allocated hours (excluding current entry if editing)
      const allocatedResult = await getTotalAllocatedHours(userData.employeeId, date)

      if (allocatedResult.success) {
        let allocated = allocatedResult.hours || 0

        // If editing, subtract current entry's hours from allocated
        if (editEntry && editEntry.date === date) {
          allocated -= editEntry.hours
        }

        setAllocatedHours(allocated)
      }
    } catch (error) {
      console.error('Load attendance hours error:', error)
    } finally {
      setCheckingHours(false)
    }
  }

  const resetForm = () => {
    setProjectId(projects.length > 0 ? projects[0].id : '')
    setDate(selectedDate || getTodayDate())
    setHours('')
    setTaskDescription('')
    setError('')
    setWorkedHours(0)
    setAllocatedHours(0)
    setHasCheckOut(false)
  }

  const calculateAvailableHours = () => {
    const hoursValue = parseFloat(hours) || 0
    const available = workedHours - allocatedHours - hoursValue
    return Math.max(0, available)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate required fields
    if (!projectId || !date || !hours || !taskDescription.trim()) {
      setError('All fields are required')
      return
    }

    // Validate hours
    const hoursValue = parseFloat(hours)

    if (isNaN(hoursValue) || hoursValue <= 0) {
      setError('Hours must be greater than 0')
      return
    }

    // Validate hours don't exceed available
    const totalAllocated = allocatedHours + hoursValue

    if (totalAllocated > workedHours) {
      const available = workedHours - allocatedHours
      setError(`Cannot allocate ${hoursValue}h. Only ${available.toFixed(2)}h available`)
      return
    }

    // Validate description length
    if (taskDescription.length > descriptionMaxLength) {
      setError(`Task description exceeds maximum length of ${descriptionMaxLength} characters`)
      return
    }

    // Validate attendance exists
    if (!hasCheckOut) {
      setError('No completed attendance record for this date. Please check in and check out first.')
      return
    }

    // Submit
    onSubmit({
      projectId,
      date,
      hours: hoursValue,
      taskDescription: taskDescription.trim()
    })
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  if (!isOpen) return null

  const availableHours = workedHours - allocatedHours
  const isEditMode = !!editEntry

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div className="psyber-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className={isEditMode ? 'fas fa-edit' : 'fas fa-plus'}></i>
            {isEditMode ? 'Edit Timesheet Entry' : 'Add Timesheet Entry'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Available Hours Banner */}
          {date && hasCheckOut && !checkingHours && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: availableHours > 0 ? 'var(--psyber-info-bg)' : 'var(--psyber-success-bg)',
              border: availableHours > 0 ? '1px solid var(--psyber-info)' : '1px solid var(--psyber-success)',
              borderRadius: 'var(--psyber-radius-md)'
            }}>
              <div style={{
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)',
                marginBottom: '0.5rem'
              }}>
                📊 Date Summary for {formatDate(date)}:
              </div>
              <div style={{
                fontSize: 'var(--psyber-font-size-sm)',
                color: 'var(--psyber-text)'
              }}>
                • Worked: <strong>{workedHours.toFixed(2)}h</strong> |
                Already Allocated: <strong>{allocatedHours.toFixed(2)}h</strong> |
                Available: <strong>{availableHours.toFixed(2)}h</strong>
              </div>
            </div>
          )}

          {/* Warning: No attendance */}
          {date && !hasCheckOut && !checkingHours && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-warning-bg)',
              border: '2px solid var(--psyber-warning)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.5rem', color: 'var(--psyber-warning)' }}></i>
              <span style={{ color: 'var(--psyber-text)', fontWeight: 600 }}>
                ⚠️ No attendance record for this date. Please check in and check out first.
              </span>
            </div>
          )}

          {/* Checking Hours Indicator */}
          {checkingHours && (
            <div style={{
              marginBottom: '1rem',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <i className="fas fa-spinner fa-spin"></i>
              Loading attendance data...
            </div>
          )}

          {/* Project Dropdown */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="projectId"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-project-diagram"></i> Project *
            </label>
            <select
              id="projectId"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={isLoading}
              className="psyber-input"
              style={{
                width: '100%',
                cursor: 'pointer'
              }}
              required
            >
              {projects.length === 0 ? (
                <option value="">Loading projects...</option>
              ) : (
                projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.projectId})
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Date and Hours Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Date Field */}
            <div>
              <label
                htmlFor="date"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-calendar-day"></i> Date *
              </label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={minDate}
                max={maxDate}
                disabled={isLoading}
                className="psyber-input"
                style={{ width: '100%' }}
                required
              />
            </div>

            {/* Hours Field */}
            <div>
              <label
                htmlFor="hours"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}
              >
                <i className="fas fa-clock"></i> Hours *
              </label>
              <input
                type="number"
                id="hours"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g., 7.5"
                step="0.25"
                min="0.25"
                disabled={isLoading}
                className="psyber-input"
                style={{ width: '100%' }}
                required
              />
            </div>
          </div>

          {/* Task Description Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="taskDescription"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-tasks"></i> Task Description *
            </label>
            <textarea
              id="taskDescription"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Describe what you worked on..."
              disabled={isLoading}
              maxLength={descriptionMaxLength}
              className="psyber-input"
              rows={6}
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: '120px'
              }}
              required
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: taskDescription.length > descriptionMaxLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {taskDescription.length}/{descriptionMaxLength} characters
            </div>
          </div>

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-info-bg)',
            border: '1px solid var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)', marginRight: '0.5rem' }}></i>
            <strong>Note:</strong> Your timesheet entry will be reviewed by your manager before approval.
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid var(--psyber-card-border)'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={isEditMode ? 'fas fa-save' : 'fas fa-plus'}
              loading={isLoading}
              disabled={isLoading || !projectId || !date || !hours || !taskDescription.trim()}
            >
              {isLoading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Entry' : 'Add Entry')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TimesheetEntryModal
